/* ════════════════════════════════════════════════════════════════
   rs-vh-lock.js — freeze viewport height on iOS Brave.

   Bug (measured 2026-08-04): on iOS Brave (NOT Safari), collapsing the
   browser chrome resizes the WKWebView itself. vh / svh / lvh / dvh all
   RE-RESOLVE under that webview resize, so viewport-sized boxes jump
   mid-scroll with zero scroll delta (~83 CSS px in one frame on the hero).

   Fix: measure the viewport height ONCE at load and publish it as the CSS
   custom property --rs-vh-lock on <html>. Consumers use it as a locked
   value with their live viewport unit as the var() fallback, so if this
   script never runs, rendering degrades to the pre-lock behaviour rather
   than breaking.

   ── REPAIR 2026-08-20 ─────────────────────────────────────────────
   The lvh measurement above had NEVER ONCE EXECUTED. The probe is an
   empty <div>, and base.css:495-509 declares `div:empty { display: none }`
   site-wide — so the probe generated NO BOX, getBoundingClientRect()
   returned 0, and the zero-guard fell through to window.innerHeight on
   100% of loads. Every lock written between 2026-08-04 and 2026-08-20 was
   therefore the SMALL viewport: the exact opposite of the intent stated
   below, and silent, because nothing distinguished a dead probe from an
   engine without lvh.

   Two changes stop it recurring: an inline `display:block` on the probe
   (see measure()), and an explicit CSS.supports() detect so "lvh
   unsupported" and "probe broken" can no longer surface as the same zero.
   ──────────────────────────────────────────────────────────────────

   ── CORRECTION 2026-08-20 (on-device readout, Brave iOS) ──────────
   THE LOCKED VALUE IS NOT CHROME-INDEPENDENT. An earlier version of this
   header claimed that measuring through 100lvh returned the chrome-
   collapsed height regardless of the chrome state at measurement time, and
   traded on that claim to describe a "we lock the LARGE viewport, not the
   small one" choice with a ~83px consequence. Measured on hardware, both
   halves are false.

   On a webview-resizing browser the WEBVIEW ITSELF changes size, so no
   large-vs-small viewport distinction survives to be expressed and EVERY
   viewport unit simply reports the current height. Read back on Brave iOS:
   100lvh and 100svh returned IDENTICAL values at both chrome states —
   both 714.0 with the toolbar up, both 833.7 after it collapsed. The probe
   therefore measures whatever the viewport is AT THAT INSTANT, and the lock
   depends on the browser chrome state AT LOAD. Observed directly: the lock
   read 714.0 on one load and 685.0 on another, purely because the tab bar
   was showing the second time.

   What this file actually does is FREEZE WHATEVER THE VIEWPORT IS AT LOAD.
   That is a real limitation of the approach, not a bug in it — describe it
   that way rather than promising a collapsed-chrome value.

   Consequences for anyone editing this file:
     - lvh vs svh is a MEANINGLESS choice on that browser. 100lvh is kept
       for consistency with the consumers listed below, not for any
       measured benefit over 100svh.
     - There is no CSS-only version of this fix. No unit is stable, which
       is the whole reason the value is measured in JS and frozen.
   ──────────────────────────────────────────────────────────────────

   Consumers should pair the var with a 100lvh fallback, NOT 100svh — but
   for a narrower reason than previously stated. The box paints at the
   fallback before this deferred script runs, so on an engine where the two
   units genuinely differ (iOS Safari) an svh fallback against a lock read
   through lvh disagrees by the toolbar height and produces a visible
   one-time jump at load. On a webview-resizing browser they are the same
   number and the choice does not matter. Matching the unit is still the
   right default; it just buys nothing on the browser this file exists for.
   Only the homepage hero has been brought to parity so far — the other
   consumers below still carry their original fallbacks and will show that
   jump on Safari until they are migrated.

   No scrollY guard — AND DELETING IT IS WHAT ACTUALLY STOPPED THE DRIFT.
   An earlier version wrote the property only while scrollY === 0. That
   guard opened a failure path where a browser restoring scroll position on
   reload left scrollY non-zero at BOTH write opportunities: the property
   was never written at all, every consumer silently fell through to a
   live-resolving unit, and the boxes resized mid-scroll exactly as they
   had before the lock existed. Removing it (2026-08-20) is the change that
   fixed the observed resizing.

   Do NOT credit the probe repair above with that fix. With the probe dead
   the fallback was window.innerHeight, which on this browser equals what
   the repaired probe now returns — so the repair did not change the locked
   number here. It was still correct: it removed a silent failure and made
   the value intentional rather than accidental, and it matters on an engine
   where the units DO differ. But it is not what stopped the resizing, and
   the reason the guard was originally removed ("the value is chrome-
   independent") was itself false — see the correction above.

   Recompute policy: ONLY on a viewport WIDTH change or orientationchange.
   Height-only resizes (the Brave chrome collapse) are ignored — that is
   the entire point. innerWidth is cached and compared on every resize.

   Consuming surfaces:
     - Homepage hero      sections/rs-teaser-hero.liquid  (--rs-vh-lock, 100lvh)
     - Homepage calm vid  sections/rs-calm-video.liquid   (--rs-vh-lock, 100vh)
     - Gallery hero       sections/rs-gallery-hero.liquid (--rs-vh-lock, 100vh)
     - Our Silk .os-hero  assets/our-silk.css             (--rs-vh-lock, 100vh)
                          (enqueued by sections/our-silk-story.liquid)
     - sections/image-banner.liquid also consumes and enqueues this, but is
       RETIRED — referenced by no template. Listed so the grep hit is not a
       surprise; it is not a live surface.
   ════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  // Guard against double-load: two consuming sections can appear on one
  // page (hero + calm-video on the homepage), so the tag may be enqueued
  // twice. Run the lock logic once.
  if (window.__rsVhLockInit) return;
  window.__rsVhLockInit = true;

  // Cache innerWidth up front so height-only resizes are ignored.
  var lastWidth = window.innerWidth;

  // Detect lvh ONCE, explicitly. Keeping this separate from the reading in
  // measure() is the whole point of the 2026-08-20 repair: previously an
  // unsupported unit and a probe that generated no box both surfaced as
  // h === 0 and both fell through to innerHeight, so a dead probe was
  // indistinguishable from an old engine and went unnoticed for months.
  var LVH_SUPPORTED =
    typeof window.CSS !== 'undefined' &&
    window.CSS !== null &&
    typeof window.CSS.supports === 'function' &&
    window.CSS.supports('height', '100lvh');

  // Write the locked height, read through a temporary 100lvh probe element.
  // This does NOT guarantee the chrome-collapsed height: on a webview-
  // resizing browser lvh reports the CURRENT viewport, so the value is
  // whatever the viewport is at this instant. See the header correction.
  function measure() {
    var el = document.documentElement;
    if (!el) return; // fail silently

    var h;

    if (!LVH_SUPPORTED) {
      // Genuinely no lvh in this engine. innerHeight is the best available
      // approximation, and it is NOT necessarily worse than the probe path:
      // on a webview-resizing browser the two agree, because lvh reports the
      // current viewport there too. They diverge only on an engine where the
      // large and small viewports are genuinely distinct (iOS Safari), where
      // innerHeight tracks the current chrome state and the probe would have
      // returned the large viewport.
      h = window.innerHeight;
    } else {
      var probe = document.createElement('div');

      // `display:block` IS LOAD-BEARING — DO NOT DELETE AS REDUNDANT.
      // base.css:495-509 declares `div:empty { display: none }` site-wide at
      // (0,1,1), and this probe is an empty div, so the rule matches it. The
      // inline style wins because it declares the property the rule targets;
      // without it the probe generates no box at all and the rect reads 0.
      // This is the bug that made the lvh measurement inert from 2026-08-04
      // to 2026-08-20. Same Dawn trap documented in CLAUDE.md, and the same
      // one worked around by .rs-sp__status:empty in assets/rs-menu.css.
      probe.style.cssText =
        'display:block;height:100lvh;position:absolute;visibility:hidden;pointer-events:none;top:0;left:0;';

      el.appendChild(probe);
      h = probe.getBoundingClientRect().height;
      el.removeChild(probe);

      // Second line of defence, NOT the lvh detect — that is LVH_SUPPORTED
      // above. Reaching this branch now means THE PROBE ITSELF IS BROKEN
      // AGAIN: a new empty-element rule matching it, an ancestor that stops
      // it laying out, or an engine returning 0 for a hidden box. If you are
      // debugging a hero that locks to the wrong height, start here — and do
      // not "simplify" this back into the feature detect, because collapsing
      // the two is precisely what hid the original failure.
      if (!isFinite(h) || h <= 0) {
        h = window.innerHeight;
      }
    }

    el.style.setProperty('--rs-vh-lock', h.toFixed(1) + 'px');
    lastWidth = window.innerWidth;
  }

  // Resize: recompute ONLY when the WIDTH changed. Height-only changes
  // (Brave chrome collapse) return immediately without rewriting.
  function onResize() {
    if (window.innerWidth === lastWidth) return;
    measure();
  }

  // Unconditional at parse and on load — no scrollY guard (see header).
  // The load run catches any late layout shift that changes the large
  // viewport; on a stable page it rewrites the same value.
  measure();
  window.addEventListener('load', measure);
  window.addEventListener('resize', onResize, { passive: true });
  // Orientation change always implies a width change — force a recompute
  // (fires even where the resize width comparison races the metrics update).
  window.addEventListener('orientationchange', measure);
})();
